import React, { useState } from 'react';
import { Box, MenuItem, Select, Typography } from '@mui/material';
import { GeographicRegion } from '../../graphql/pimcore/modules/geographic/types/GeographicRegion';
import { useResponsive } from '../../contexts/ResponsiveContext';
import { SalesAndResourcesContactPointsContent } from '../../graphql/pimcore/modules/siteContent/types/SalesAndResourcesContactPointsContent';

interface LocationSelectProps {
    geographicRegions: GeographicRegion[];
    setSelectedCountryId: (countries: string[]) => void;
    setSelectedGeographicRegionId: (regions: string[]) => void;
    ignoreRegion?: boolean;
    contentTree: SalesAndResourcesContactPointsContent;
}

const LocationSelect: React.FC<LocationSelectProps> = ({
    geographicRegions,
    setSelectedGeographicRegionId,
    setSelectedCountryId,
    ignoreRegion,
    contentTree,
}) => {
    const { isScreenXl, isScreenLg, isScreenMd, isScreenSm, isScreenXs } = useResponsive();
    const [region, setRegion] = useState<string | null>(ignoreRegion ?? false ? 'all' : null);
    const [country, setCountry] = useState<string | null>(null);

    const allCountries = geographicRegions.flatMap((r) => r.countries.map((c) => c.id));

    const handleDeleteRegion = () => {
        ignoreRegion && setRegion(null);
        setSelectedGeographicRegionId([]);
        setCountry(null);
        setSelectedCountryId([]);
    };

    const handleDeleteCountry = () => {
        setCountry(null);
        setSelectedCountryId([]);
    };

    const onCountryClick = (clickedCountryId: string) => {
        if (clickedCountryId === country) {
            setCountry(null);
            setSelectedCountryId([]);
        } else {
            setCountry(clickedCountryId);
            setSelectedCountryId([clickedCountryId]);
        }
    };

    const onRegionClick = (clickedRegionId: string) => {
        if (clickedRegionId === region) {
            handleDeleteRegion();
        } else {
            setRegion(clickedRegionId);
            setSelectedGeographicRegionId([clickedRegionId]);

            const validCountries =
                clickedRegionId === 'all'
                    ? allCountries
                    : geographicRegions
                          .find((r) => r.id === clickedRegionId)
                          ?.countries.map((c) => c.id) || [];

            if (!validCountries.includes(country || '')) {
                setCountry(null);
                setSelectedCountryId([]);
            }
        }
    };

    return (
        <Box
            className="location-select-box"
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: isScreenSm || isScreenXs ? 'column' : 'row',
                gap: '1rem',
                paddingBottom: '1rem',
                paddingTop: '1rem',
            }}
        >
            {region !== 'all' && (
                <Box sx={{ flexDirection: 'column', gap: '1rem', flex: '1 1 calc(50% - 1rem)' }}>
                    <Typography sx={{ fontFamily: 'Poppins', marginBottom: '0.5rem' }}>
                        {contentTree.common.regionSelectPlaceholder.content}
                    </Typography>
                    <Select
                        fullWidth
                        value={region || ''}
                        onChange={() => {}}
                        variant="outlined"
                        displayEmpty
                        sx={{ backgroundColor: 'white' }}
                    >
                        <MenuItem disabled value="">
                            <span> {contentTree.common.regionSelectLabel.content}</span>
                        </MenuItem>
                        {geographicRegions
                            .slice()
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((option) => (
                                <MenuItem
                                    key={option.id}
                                    onClick={() => onRegionClick(option.id)}
                                    selected={region === option.id}
                                    value={option.id}
                                >
                                    {option.name}
                                </MenuItem>
                            ))}
                    </Select>
                </Box>
            )}

            <Box sx={{ flex: '1 1 calc(50% - 1rem)', flexDirection: 'column', gap: '1rem' }}>
                <Typography sx={{ fontFamily: 'Poppins', marginBottom: '0.5rem' }}>
                    {contentTree.common.countrySelectPlaceholder.content}
                </Typography>
                <Select
                    fullWidth
                    value={country || ''}
                    onChange={() => {}}
                    variant="outlined"
                    displayEmpty
                    sx={{ backgroundColor: 'white' }}
                >
                    <MenuItem disabled value="">
                        <span>{contentTree.common.countrySelectLabel.content}</span>
                    </MenuItem>
                    {(region === 'all'
                        ? allCountries
                        : geographicRegions
                              .find((r) => r.id === region)
                              ?.countries.map((c) => c.id) || []
                    )
                        .slice()
                        .sort((a, b) => {
                            const countryA =
                                geographicRegions
                                    .flatMap((r) => r.countries)
                                    .find((c) => c.id === a)?.name || '';
                            const countryB =
                                geographicRegions
                                    .flatMap((r) => r.countries)
                                    .find((c) => c.id === b)?.name || '';
                            return countryA.localeCompare(countryB);
                        })
                        .map((countryId) => {
                            const countryData = geographicRegions
                                .flatMap((r) => r.countries)
                                .find((c) => c.id === countryId);
                            return (
                                <MenuItem
                                    key={countryId}
                                    onClick={() => onCountryClick(countryId)}
                                    selected={countryId === country}
                                    value={countryId}
                                >
                                    {countryData?.name}
                                </MenuItem>
                            );
                        })}
                </Select>
            </Box>
        </Box>
    );
};

export default LocationSelect;
const countryList = [
    'Afghanistan',
    'Albania',
    'Algeria',
    'Andorra',
    'Angola',
    'Antigua and Barbuda',
    'Argentina',
    'Armenia',
    'Australia',
    'Austria',
    'Azerbaijan',
    'Bahamas',
    'Bahrain',
    'Bangladesh',
    'Barbados',
    'Belarus',
    'Belgium',
    'Belize',
    'Benin',
    'Bhutan',
    'Bolivia',
    'Bosnia and Herzegovina',
    'Botswana',
    'Brazil',
    'Brunei',
    'Bulgaria',
    'Burkina Faso',
    'Burundi',
    'Cabo Verde',
    'Cambodia',
    'Cameroon',
    'Canada',
    'Central African Republic',
    'Chad',
    'Chile',
    'China',
    'Colombia',
    'Comoros',
    'Congo (Congo-Brazzaville)',
    'Costa Rica',
    "Côte d'Ivoire (Ivory Coast)",
    'Croatia',
    'Cuba',
    'Cyprus',
    'Czech Republic',
    'Denmark',
    'Djibouti',
    'Dominica',
    'Dominican Republic',
    'East Timor (Timor-Leste)',
    'Ecuador',
    'Egypt',
    'El Salvador',
    'Equatorial Guinea',
    'Eritrea',
    'Estonia',
    'Eswatini (Swaziland)',
    'Ethiopia',
    'Fiji',
    'Finland',
    'France',
    'Gabon',
    'Gambia',
    'Georgia',
    'Germany',
    'Ghana',
    'Greece',
    'Grenada',
    'Guatemala',
    'Guinea',
    'Guinea-Bissau',
    'Guyana',
    'Haiti',
    'Honduras',
    'Hungary',
    'Iceland',
    'India',
    'Indonesia',
    'Iran',
    'Iraq',
    'Ireland',
    'Israel',
    'Italy',
    'Jamaica',
    'Japan',
    'Jordan',
    'Kazakhstan',
    'Kenya',
    'Kiribati',
    'North Korea',
    'South Korea',
    'Kosovo',
    'Kuwait',
    'Kyrgyzstan',
    'Laos',
    'Latvia',
    'Lebanon',
    'Lesotho',
    'Liberia',
    'Libya',
    'Liechtenstein',
    'Lithuania',
    'Luxembourg',
    'Madagascar',
    'Malawi',
    'Malaysia',
    'Maldives',
    'Mali',
    'Malta',
    'Marshall Islands',
    'Mauritania',
    'Mauritius',
    'Mexico',
    'Micronesia',
    'Moldova',
    'Monaco',
    'Mongolia',
    'Montenegro',
    'Morocco',
    'Mozambique',
    'Myanmar (Burma)',
    'Namibia',
    'Nauru',
    'Nepal',
    'Netherlands',
    'New Zealand',
    'Nicaragua',
    'Niger',
    'Nigeria',
    'North Macedonia',
    'Norway',
    'Oman',
    'Pakistan',
    'Palau',
    'Palestine',
    'Panama',
    'Papua New Guinea',
    'Paraguay',
    'Peru',
    'Philippines',
    'Poland',
    'Portugal',
    'Qatar',
    'Romania',
    'Russia',
    'Rwanda',
    'Saint Kitts and Nevis',
    'Saint Lucia',
    'Saint Vincent and the Grenadines',
    'Samoa',
    'San Marino',
    'São Tomé and Príncipe',
    'Saudi Arabia',
    'Senegal',
    'Serbia',
    'Seychelles',
    'Sierra Leone',
    'Singapore',
    'Slovakia',
    'Slovenia',
    'Solomon Islands',
    'Somalia',
    'South Africa',
    'South Sudan',
    'Spain',
    'Sri Lanka',
    'Sudan',
    'Suriname',
    'Sweden',
    'Switzerland',
    'Syria',
    'Tajikistan',
    'Tanzania',
    'Thailand',
    'Togo',
    'Tonga',
    'Trinidad and Tobago',
    'Tunisia',
    'Turkey',
    'Turkmenistan',
    'Tuvalu',
    'Uganda',
    'Ukraine',
    'United Arab Emirates',
    'United Kingdom',
    'United States',
    'Uruguay',
    'Uzbekistan',
    'Vanuatu',
    'Vatican City (Holy See)',
    'Venezuela',
    'Vietnam',
    'Yemen',
    'Zambia',
    'Zimbabwe',
];
